import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Shield, Activity, Eye, EyeOff } from 'lucide-react';
import { useAuthWithRole } from '../hooks/useAuthWithRole';
import { hasPermission, ADMIN_CONFIG } from '../config/admin';
import {
  getAuthorizedUsers,
  createAuthorizedUser,
  updateAuthorizedUser,
  deleteAuthorizedUser,
  getActivityLogs,
  AuthorizedUser,
  ActivityLog
} from '../utils/firestoreApi';

// Use the imported types from firestoreApi

const AdminPanel: React.FC = () => {
  const { userRole, isAdmin, isSuperAdmin } = useAuthWithRole();
  const [users, setUsers] = useState<AuthorizedUser[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'activity'>('users');
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState<AuthorizedUser | null>(null);

  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    displayName: '',
    role: 'user' as const
  });

  useEffect(() => {
    if (isAdmin()) {
      loadUsers();
      if (isSuperAdmin()) {
        loadActivityLogs();
      }
    }
  }, [userRole]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await getAuthorizedUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const logsData = await getActivityLogs();
      setActivityLogs(logsData);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createAuthorizedUser({
        email: newUser.email.toLowerCase(),
        displayName: newUser.displayName || undefined,
        role: newUser.role,
        isActive: true
      });
      setNewUser({ email: '', displayName: '', role: 'user' });
      setShowAddUser(false);
      loadUsers();
    } catch (error: any) {
      alert(error.message || 'Failed to add user');
    }
  };

  const handleUpdateUser = async (userEmail: string, updates: Partial<AuthorizedUser>) => {
    try {
      await updateAuthorizedUser(userEmail, updates);
      setEditingUser(null);
      loadUsers();
    } catch (error: any) {
      alert(error.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (userEmail: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await deleteAuthorizedUser(userEmail);
      loadUsers();
    } catch (error: any) {
      alert(error.message || 'Failed to delete user');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin': return <Shield className="h-4 w-4" />;
      case 'admin': return <Users className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  if (!userRole || !isAdmin()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-gray-600">Manage users and system settings</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-5 w-5 inline mr-2" />
            User Management
          </button>
          {userRole?.email === ADMIN_CONFIG.SUPER_ADMIN_EMAIL && (
            <button
              onClick={() => setActiveTab('activity')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'activity'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="h-5 w-5 inline mr-2" />
              Activity Log
            </button>
          )}
        </nav>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Authorized Users</h2>
            <button
              onClick={() => setShowAddUser(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="bg-white shadow-sm overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {users.map((userData) => (
                  <li key={userData.email} className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="shrink-0">
                          {userData.photoURL ? (
                            <img className="h-10 w-10 rounded-full" src={userData.photoURL} alt="" />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <Users className="h-6 w-6 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">
                              {userData.displayName || userData.email}
                            </p>
                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(userData.role)}`}>
                              {getRoleIcon(userData.role)}
                              <span className="ml-1">{userData.role.replace('_', ' ')}</span>
                            </span>
                            {!userData.isActive && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <EyeOff className="h-3 w-3 mr-1" />
                                Inactive
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{userData.email}</p>
                          <p className="text-xs text-gray-400">
                            Last login: {userData.lastLoginAt ? (userData.lastLoginAt instanceof Date ? userData.lastLoginAt.toLocaleString() : new Date(userData.lastLoginAt.toDate()).toLocaleString()) : 'Never'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {userData.role !== 'super_admin' && (
                          <>
                            <button
                              onClick={() => setEditingUser(userData)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(userData.email)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-6">System Activity Log</h2>
          <div className="bg-white shadow-sm overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {activityLogs.map((log) => (
                <li key={log.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {log.userEmail} - {log.action.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-500">
                        {log.createdAt instanceof Date ? log.createdAt.toLocaleString() : new Date(log.createdAt.toDate()).toLocaleString()}
                      </p>
                      {log.details && (
                        <p className="text-xs text-gray-400 mt-1">
                          {JSON.stringify(log.details)}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <form onSubmit={handleAddUser}>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Add New User</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Display Name
                </label>
                <input
                  type="text"
                  value={newUser.displayName}
                  onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {userRole?.email === ADMIN_CONFIG.SUPER_ADMIN_EMAIL && (
                    <option value="super_admin">Super Admin</option>
                  )}
                </select>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAddUser(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name
              </label>
              <input
                type="text"
                value={editingUser.displayName || ''}
                onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={editingUser.role}
                onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as any })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {userRole?.email === ADMIN_CONFIG.SUPER_ADMIN_EMAIL && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>
            <div className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingUser.isActive}
                  onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.checked })}
                  className="mr-2"
                />
                Active
              </label>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateUser(editingUser.email, {
                  displayName: editingUser.displayName,
                  role: editingUser.role,
                  isActive: editingUser.isActive
                })}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;